import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/Button";
import { ClickableQrCode } from "@/components/ui/ClickableQrCode";
import { Dialog } from "@/components/ui/Dialog";
import { SignatureCanvas } from "@/features/timesheetFlow/components/SignatureCanvas";
import {
  hasBlockingSiblingTimesheetForProject,
  isMobileDevice,
  projectCloseConflictMessage,
  type TimesheetProjectCloseContext,
} from "@/features/timesheetFlow/lib/timesheetProjectClose";
import {
  closeProject,
  getCompanySignQr,
  getTimesheetDetail,
  normalizePortalUrl,
  signCompany,
  uploadTimesheetPdf,
} from "@/features/timesheetFlow/timesheetFlowApi";
import { ApiError } from "@/lib/apiClient";
import { listTimesheetsSummary } from "@/lib/timesheets";
import { useTimesheetFlowLayers } from "@/features/timesheetFlow/timesheetFlowLayers";

type FinalizePhase = "projectClose" | "signature";

export function TimesheetFinalizeFlow({
  timesheetId,
  projectCloseContext,
  onComplete,
  onDismiss,
}: {
  timesheetId: number | null;
  projectCloseContext: TimesheetProjectCloseContext | null;
  onComplete: (id: number) => void;
  onDismiss: () => void;
}) {
  const layers = useTimesheetFlowLayers();
  const [phase, setPhase] = useState<FinalizePhase | null>(null);
  const [projectCloseBlocked, setProjectCloseBlocked] = useState(false);
  const [projectCloseBlockedNotice, setProjectCloseBlockedNotice] = useState(false);
  const [projectCloseConfirmStep, setProjectCloseConfirmStep] = useState(0);
  const [closingProject, setClosingProject] = useState(false);
  const [projectCloseError, setProjectCloseError] = useState<string | null>(null);

  const [signDataUrl, setSignDataUrl] = useState<string | null>(null);
  const [signSubmitting, setSignSubmitting] = useState(false);
  const [signError, setSignError] = useState<string | null>(null);
  const [qrUri, setQrUri] = useState<string | null>(null);
  const [qrUrl, setQrUrl] = useState<string | null>(null);
  const [iosAppSignedIn, setIosAppSignedIn] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const resetState = useCallback(() => {
    setPhase(null);
    setProjectCloseBlocked(false);
    setProjectCloseBlockedNotice(false);
    setProjectCloseConfirmStep(0);
    setClosingProject(false);
    setProjectCloseError(null);
    setSignDataUrl(null);
    setSignSubmitting(false);
    setSignError(null);
    setQrUri(null);
    setQrUrl(null);
    setIosAppSignedIn(false);
    stopPolling();
  }, [stopPolling]);

  const refreshProjectCloseBlocking = useCallback(async () => {
    if (!timesheetId || !projectCloseContext) return false;
    const list = await listTimesheetsSummary().catch(() => []);
    const blocked = hasBlockingSiblingTimesheetForProject(timesheetId, list, projectCloseContext);
    setProjectCloseBlocked(blocked);
    return blocked;
  }, [timesheetId, projectCloseContext]);

  useEffect(() => {
    if (!timesheetId || !projectCloseContext) {
      resetState();
      return;
    }

    setPhase("projectClose");
    setProjectCloseConfirmStep(0);
    setProjectCloseError(null);
    setProjectCloseBlockedNotice(false);

    let cancelled = false;
    void (async () => {
      const blocked = await refreshProjectCloseBlocking();
      if (cancelled) return;
      if (blocked) {
        setProjectCloseBlockedNotice(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [timesheetId, projectCloseContext, resetState, refreshProjectCloseBlocking]);

  const openSignature = useCallback(async () => {
    if (!timesheetId) return;
    setPhase("signature");
    setSignDataUrl(null);
    setSignError(null);
    stopPolling();

    const showQr = !isMobileDevice();
    try {
      const data = await getCompanySignQr(timesheetId);
      setIosAppSignedIn(Boolean(data.ios_app_signed_in));
      setQrUri(showQr ? data.qr_data_uri || null : null);
      setQrUrl(showQr && data.url ? normalizePortalUrl(data.url) : null);
    } catch {
      setIosAppSignedIn(false);
      setQrUri(null);
      setQrUrl(null);
    }

    if (showQr) {
      pollRef.current = setInterval(() => {
        void getTimesheetDetail(timesheetId).then((bundle) => {
          const ts = bundle.timesheet;
          const companySigned = Boolean(ts.company_signed_at || ts.status === "ready" || ts.status === "signed");
          if (!companySigned) return;

          stopPolling();
          setPhase(null);
          onComplete(timesheetId);
        });
      }, 2000);
    }
  }, [timesheetId, onComplete, stopPolling]);

  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  const handleDismiss = () => {
    resetState();
    onDismiss();
  };

  const handleProjectCloseNo = () => {
    void openSignature();
  };

  const handleProjectCloseYes = async () => {
    if (!timesheetId) return;

    const blocked = projectCloseBlocked || (await refreshProjectCloseBlocking());
    if (blocked) {
      setProjectCloseBlockedNotice(true);
      return;
    }

    if (projectCloseConfirmStep === 0) {
      setProjectCloseConfirmStep(1);
      return;
    }

    setClosingProject(true);
    setProjectCloseError(null);
    try {
      await closeProject(timesheetId);
      await openSignature();
    } catch (err: unknown) {
      if (err instanceof ApiError && err.status === 409) {
        setProjectCloseBlocked(true);
        setProjectCloseBlockedNotice(true);
        setProjectCloseConfirmStep(0);
      }
      const message =
        err instanceof ApiError
          ? err.message.trim() || projectCloseConflictMessage(err.status)
          : err instanceof Error && err.message.trim()
            ? err.message.trim()
            : projectCloseConflictMessage();
      setProjectCloseError(message);
    } finally {
      setClosingProject(false);
    }
  };

  const handleSignSubmit = async () => {
    if (!timesheetId || !signDataUrl) return;
    setSignSubmitting(true);
    setSignError(null);
    stopPolling();
    try {
      await signCompany(timesheetId, signDataUrl);
      await uploadTimesheetPdf(timesheetId);
      setPhase(null);
      onComplete(timesheetId);
    } catch (err: unknown) {
      setSignError(err instanceof Error ? err.message : "Unterschrift fehlgeschlagen.");
      if (!isMobileDevice()) {
        pollRef.current = setInterval(() => {
          void getTimesheetDetail(timesheetId).then((bundle) => {
            const ts = bundle.timesheet;
            const companySigned = Boolean(ts.company_signed_at || ts.status === "ready" || ts.status === "signed");
            if (!companySigned) return;
            stopPolling();
            setPhase(null);
            onComplete(timesheetId);
          });
        }, 2000);
      }
    } finally {
      setSignSubmitting(false);
    }
  };

  const showProjectClose = Boolean(timesheetId && phase === "projectClose");
  const showSignature = Boolean(timesheetId && phase === "signature");

  return (
    <>
      <Dialog
        open={showProjectClose}
        onClose={handleDismiss}
        title="Projekt abschließen?"
        size="md"
        zIndexClass={layers.overlay}
        fullScreenMobile
        footer={
          <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button type="button" variant="secondary" onClick={handleDismiss} disabled={closingProject}>
              Zurück
            </Button>
            <Button type="button" variant="secondary" onClick={handleProjectCloseNo} disabled={closingProject}>
              Nein, es kommen noch Arbeiten
            </Button>
            {!projectCloseBlocked ? (
              <Button type="button" onClick={() => void handleProjectCloseYes()} disabled={closingProject}>
                {projectCloseConfirmStep === 0 ? "Ja, Projekt abschließen" : "Ja, trotzdem abschließen"}
              </Button>
            ) : null}
          </div>
        }
      >
        <div className="space-y-3 text-sm text-label-secondary">
          <p>
            Sind mit diesem Stundennachweis <strong className="text-label">alle Arbeiten in diesem Projekt abgeschlossen</strong>?
          </p>
          {projectCloseBlockedNotice && projectCloseBlocked ? (
            <p className="rounded-ios bg-fill-tertiary px-3 py-2 text-label">
              Projektabschluss gesperrt, da ein weiterer Stundennachweis im Status Entwurf für dieses Projekt existiert.
            </p>
          ) : null}
          {projectCloseBlocked ? (
            <p className="rounded-ios bg-status-red/10 px-3 py-2 text-status-red">
              <strong>Projektabschluss nicht möglich:</strong> Solange ein weiterer Stundennachweis im Status Entwurf
              für dieses Projekt existiert, kann das Projekt hier nicht abgeschlossen werden.
            </p>
          ) : null}
          {projectCloseConfirmStep === 1 && !projectCloseBlocked ? (
            <p className="rounded-ios bg-status-yellow/10 px-3 py-2 text-label">
              <strong>Hinweis:</strong> Wenn du mit <em>Ja</em> bestätigst, wird dieses Projekt als abgeschlossen
              betrachtet. Sollten später noch Arbeiten anfallen, muss hierfür ein <strong>neues Projekt</strong>{" "}
              angelegt werden.
            </p>
          ) : null}
          {projectCloseError ? <p className="text-status-red">{projectCloseError}</p> : null}
        </div>
      </Dialog>

      <Dialog
        open={showSignature}
        onClose={handleDismiss}
        title="Stundennachweis unterschreiben"
        description={
          qrUri
            ? "Am PC: QR-Code mit dem Smartphone scannen und dort unterschreiben. Alternativ direkt im Feld unten."
            : "Mit Finger oder Maus im Feld unten unterschreiben."
        }
        size="lg"
        zIndexClass={layers.overlay}
        fullScreenMobile
        footer={
          <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button type="button" variant="secondary" onClick={handleDismiss} disabled={signSubmitting}>
              Abbrechen
            </Button>
            <Button type="button" onClick={() => void handleSignSubmit()} disabled={!signDataUrl || signSubmitting}>
              {signSubmitting ? "Fertigstellen…" : "Unterschrift übernehmen & PDF erzeugen"}
            </Button>
          </div>
        }
      >
        <div className="space-y-5">
          {iosAppSignedIn && qrUri ? (
            <div className="rounded-ios-lg border border-accent/30 bg-accent/5 px-4 py-3 text-sm text-label">
              <p className="font-medium">Ernsthaus iOS-App</p>
              <p className="mt-1 text-label-secondary">
                Sie sind auch in der iOS-App angemeldet. Dort können Sie den Stundennachweis direkt unterschreiben –
                ggf. erhalten Sie dazu eine Benachrichtigung auf Ihrem iPhone.
              </p>
            </div>
          ) : null}

          {qrUri ? (
            <div className="flex flex-col items-center gap-3 rounded-ios-lg border border-separator bg-fill-tertiary/60 px-4 py-5">
              <ClickableQrCode
                src={qrUri}
                href={qrUrl}
                alt="QR-Code zum Unterschreiben"
                className="h-44 w-44 rounded-ios border border-separator bg-white p-2 shadow-sm"
              />
              <p className="text-center text-xs text-label-secondary">QR-Code mit dem Smartphone scannen</p>
            </div>
          ) : null}

          <div className="rounded-ios-lg border border-separator bg-card-elevated p-4">
            <p className="mb-3 text-sm font-medium text-label">Unterschrift</p>
            <SignatureCanvas onChange={(_, url) => setSignDataUrl(url)} />
          </div>

          {signError ? <p className="text-sm text-status-red">{signError}</p> : null}
        </div>
      </Dialog>
    </>
  );
}
